import doneUrl from '../assets/sounds/done.wav';
import startUrl from '../assets/sounds/start.wav';
import tickUrl from '../assets/sounds/tick.wav';
import { soundEffectsPreference } from './storage';

/**
 * The recorder's three sounds: a bell for each number of the countdown, a
 * brighter bell the moment recording begins, and a two-note chime when a take
 * has been saved. The same files ship in the Mac, Windows and Chrome
 * recorders, so a person who uses more than one hears the same thing from
 * each.
 *
 * All of them follow a click (Record, then Stop), so the browser's autoplay
 * rules are satisfied. A failure to play is swallowed: a sound is a nicety,
 * and nothing about the take depends on it.
 */

export type SoundEffect = 'tick' | 'start' | 'done';

const URLS: Record<SoundEffect, string> = { tick: tickUrl, start: startUrl, done: doneUrl };
const cache = new Map<SoundEffect, HTMLAudioElement>();

function element(name: SoundEffect): HTMLAudioElement {
  let audio = cache.get(name);
  if (!audio) {
    audio = new Audio(URLS[name]);
    audio.preload = 'auto';
    cache.set(name, audio);
  }
  return audio;
}

/** Fetch every file so the first tick is not late. Call from the recorder as it arms. */
export function warmSounds(): void {
  if (!soundEffectsPreference.get()) return;
  for (const name of Object.keys(URLS) as SoundEffect[]) element(name).load();
}

/** Play one, unless the person turned sounds off in Settings. */
export function playSound(name: SoundEffect, { force = false } = {}): void {
  if (!force && !soundEffectsPreference.get()) return;
  try {
    const audio = element(name);
    audio.currentTime = 0;
    void audio.play().catch(() => {});
  } catch {
    // No audio output, or a browser that refused: the take goes on regardless.
  }
}
